import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Settings — integration credentials API client (CLE-139).
//
// Mirrors backend/src/routes/settings.ts:
//   GET    /api/settings/integrations               — list all integrations + status
//   GET    /api/settings/integrations/:key          — one integration (no secrets)
//   PUT    /api/settings/integrations/:key          — save/update credentials
//   POST   /api/settings/integrations/:key/test     — run Test Connection
//   DELETE /api/settings/integrations/:key          — clear the whole credential
//   DELETE /api/settings/integrations/:key/:field   — clear one stored field
//   DELETE /api/settings/integrations?confirm=all   — wipe every credential
//
// Secret values are NEVER returned in clear: stored secrets come back masked
// ("••••••••"). The form sends only the fields the user typed; leaving a secret
// blank/masked preserves the stored value.
//
// All of these are ADMIN-only on the server.
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type IntegrationFieldType =
  | 'text'
  | 'password'
  | 'number'
  | 'boolean'
  | 'textarea'
  | 'select';

export interface IntegrationFieldOption {
  value: string;
  label: string;
}

export interface IntegrationField {
  key: string;
  label: string;
  type: IntegrationFieldType;
  secret: boolean;
  required: boolean;
  placeholder?: string;
  options?: IntegrationFieldOption[];
  /** Filled by the platform (OAuth callback); never rendered as an input. */
  platform?: boolean;
}

export type IntegrationStatus = 'connected' | 'configured' | 'not_configured' | 'error';

export type IntegrationScope = 'global' | 'company' | 'user';

export interface SettingsIntegration {
  key: string;
  label: string;
  description: string;
  scope: IntegrationScope;
  fields: IntegrationField[];
  status: IntegrationStatus;
  source: 'db' | 'env' | 'none';
  /**
   * Whether a credential row exists in the DB, even one that can't be read.
   * `hasStoredCredentials && source !== 'db'` means the stored row failed to
   * decrypt (rotated encryption secret) — it still needs a delete button.
   */
  hasStoredCredentials: boolean;
  /**
   * Anthropic / OpenAI: the key comes with the customer's Firmio subscription
   * (set in the Firmio Console). Shown as included, not editable. Absent on
   * older backends.
   */
  managedByFirmio?: boolean;
  isConnected: boolean;
  lastTestedAt: string | null;
  lastTestResult: string | null;
  /** Per-field display value: real for non-secret, "••••••••" for stored secrets, '' if empty. */
  values: Record<string, string>;
}

export interface TestResult {
  ok: boolean;
  message: string;
}

export type ConfigInput = Record<string, string | number | boolean>;

export async function listIntegrations(scope?: IntegrationScope): Promise<SettingsIntegration[]> {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  const res = await apiFetch<Envelope<{ integrations: SettingsIntegration[] }>>(
    `/api/settings/integrations${qs}`,
    { headers: authHeaders() },
  );
  return res.data.integrations;
}

/**
 * Optional per-firm scope (multi-company). Only per-company integrations
 * (SmartBill) accept it; undefined = global / default firm.
 */
function companyQs(companyId?: string): string {
  return companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
}

export async function getIntegration(
  key: string,
  companyId?: string,
): Promise<SettingsIntegration> {
  const res = await apiFetch<Envelope<{ integration: SettingsIntegration }>>(
    `/api/settings/integrations/${encodeURIComponent(key)}${companyQs(companyId)}`,
    { headers: authHeaders() },
  );
  return res.data.integration;
}

export async function saveIntegration(
  key: string,
  config: ConfigInput,
  companyId?: string,
): Promise<SettingsIntegration> {
  const res = await apiFetch<Envelope<{ integration: SettingsIntegration }>>(
    `/api/settings/integrations/${encodeURIComponent(key)}${companyQs(companyId)}`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(config) },
  );
  return res.data.integration;
}

export async function testIntegration(
  key: string,
  config: ConfigInput,
  companyId?: string,
): Promise<TestResult> {
  const res = await apiFetch<Envelope<TestResult>>(
    `/api/settings/integrations/${encodeURIComponent(key)}/test${companyQs(companyId)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(config) },
  );
  return res.data;
}

/**
 * Clear a single stored credential field (CLE-171). Drops the field from the
 * encrypted DB config; if it was the last field, the whole row is removed and
 * resolution falls back to .env. Returns the refreshed (secret-free) view.
 */
export async function deleteIntegrationField(
  key: string,
  field: string,
  companyId?: string,
): Promise<SettingsIntegration> {
  const res = await apiFetch<Envelope<{ integration: SettingsIntegration }>>(
    `/api/settings/integrations/${encodeURIComponent(key)}/${encodeURIComponent(field)}${companyQs(companyId)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data.integration;
}

/**
 * Delete an integration's whole stored credential in one request.
 *
 * One request, not one per field: a partially-cleared credential (say, SMTP
 * with the password still stored) is the worst outcome of "delete my
 * credentials". It also works on a row that no longer decrypts, which the
 * per-field route cannot.
 */
export async function deleteIntegration(
  key: string,
  companyId?: string,
): Promise<SettingsIntegration> {
  const res = await apiFetch<Envelope<{ integration: SettingsIntegration }>>(
    `/api/settings/integrations/${encodeURIComponent(key)}${companyQs(companyId)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data.integration;
}

/**
 * Wipe every stored integration credential, for every firm. `confirm=all` is
 * required by the server so a stray `DELETE /integrations/` can't trigger it.
 * Integrations that also have `.env` values keep working from `.env`.
 */
export async function deleteAllIntegrations(): Promise<{ deleted: number }> {
  const res = await apiFetch<Envelope<{ deleted: number }>>(
    '/api/settings/integrations?confirm=all',
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

// --- Knowledge Base regeneration (CLE-144) ---------------------------------

export interface RegenerateKbResult {
  clients: number;
  projectsWithFinancials: number;
  firmCostRows: number;
  /** Monthly AI financial reports (re)generated (CLE-145). */
  monthlyReports: number;
  /** Whether AI-enhanced summaries were generated (Anthropic configured). */
  aiEnabled: boolean;
  /** Suppliers projected (index + pages). */
  suppliers: number;
  /** Whether the leads index was rebuilt. */
  leadsIndex: boolean;
  /** Monthly bank digests rebuilt. */
  bankMonths: number;
  /** Yearly SmartBill rollups rebuilt. */
  smartbillYears: number;
  /** Projects whose tasks.md was rebuilt. */
  projectsWithTasks: number;
  /** Total chunks in the vector index after the full reindex. */
  reindexedChunks: number;
  /** Files mirrored to Google Drive, or null when Drive is not configured. */
  driveMirrored: number | null;
}

/** Full KB sync: DB-first projections + ingestion backfill + Drive mirror. */
export async function regenerateKnowledgeBase(): Promise<RegenerateKbResult> {
  const res = await apiFetch<Envelope<RegenerateKbResult>>('/api/settings/kb/regenerate', {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

// --- Full app backup to Google Drive ---------------------------------------

export interface BackupArtifact {
  key: string;
  fileId: string;
  bytes: number;
}

export interface BackupRunResult {
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  database: BackupArtifact;
  knowledgeBase: BackupArtifact & { files: number };
}

export interface BackupEntry {
  key: string;
  name: string;
  size: number | null;
  modifiedTime: string | null;
}

export interface BackupLastRun {
  at: string;
  ok: boolean;
  error: string | null;
  databaseKey: string | null;
  knowledgeBaseKey: string | null;
  durationMs: number | null;
}

export interface BackupSchedule {
  /** Armed on the server (BACKUP_DAILY_ENABLED). */
  enabled: boolean;
  /** HH:MM wall clock in `timeZone`. */
  time: string;
  timeZone: string;
  nextRunAt: string | null;
  /** The last unattended run, success or failure; null until the first. */
  lastRun: BackupLastRun | null;
}

export interface BackupStatus {
  driveConfigured: boolean;
  running: boolean;
  /** Newest first; cron, manual and daily backups alike. */
  backups: BackupEntry[];
  driveError: string | null;
  schedule: BackupSchedule;
}

export async function getBackupStatus(): Promise<BackupStatus> {
  const res = await apiFetch<Envelope<BackupStatus>>('/api/settings/backup', {
    headers: authHeaders(),
  });
  return res.data;
}

/** Database dump + Knowledge Base archive → Drive. Synchronous; can take minutes. */
export async function runBackup(): Promise<BackupRunResult> {
  const res = await apiFetch<Envelope<BackupRunResult>>('/api/settings/backup/run', {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

// --- Bank statement defaults (CLE-153) -------------------------------------

export interface BankDefaultCategory {
  id: string;
  code: string;
  name: string;
}

export interface BankStatementDefaults {
  creditCategoryCode: string;
  debitCategoryCode: string;
}

export interface BankDefaultsResponse {
  defaults: BankStatementDefaults;
  categories: BankDefaultCategory[];
}

/** Default categories applied to every imported credit/debit line. */
export async function getBankDefaults(): Promise<BankDefaultsResponse> {
  const res = await apiFetch<Envelope<BankDefaultsResponse>>('/api/settings/bank-defaults', {
    headers: authHeaders(),
  });
  return res.data;
}

/** Update the default credit/debit categories. */
export async function saveBankDefaults(
  input: Partial<BankStatementDefaults>,
): Promise<BankStatementDefaults> {
  const res = await apiFetch<Envelope<{ defaults: BankStatementDefaults }>>(
    '/api/settings/bank-defaults',
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data.defaults;
}

// --- Upload size limit (card under Google Drive) ---------------------------

export interface UploadLimit {
  /** Largest single file an upload may be, in bytes. */
  maxUploadBytes: number;
  /** Ceiling the setting cannot exceed (storage proxy + nginx). */
  hardCapBytes: number;
  /** Floor the setting cannot go under. */
  minBytes: number;
}

/** Readable by every signed-in user — each upload checks a file against it. */
export async function getUploadLimit(): Promise<UploadLimit> {
  const res = await apiFetch<Envelope<UploadLimit>>('/api/settings/upload-limit', {
    headers: authHeaders(),
  });
  return res.data;
}

/** Admin only. `null` resets the limit to the hard cap. */
export async function saveUploadLimit(maxUploadMb: number | null): Promise<UploadLimit> {
  const res = await apiFetch<Envelope<UploadLimit>>('/api/settings/upload-limit', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ maxUploadMb }),
  });
  return res.data;
}
